
import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  UserRound,
  LogOut,
  ArrowLeft,
  FilePlus2,
  ShieldCheck,
} from "lucide-react";

import { useSession } from "../Contexts/SessionContext";

const MainViewLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Read the active Contracts session and project.
  const { sessionNo, projectNo } = useSession();

  // Logged-in user information
  const userName = localStorage.getItem("userName") || "User";

  // Read the saved user and company roles
  let savedUser: any = {};

  try {
    savedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    savedUser = {};
  }

  const userType = String(
    savedUser.userType || localStorage.getItem("userType") || ""
  )
    .trim()
    .toLowerCase();

  const isSuperAdmin =
    userType === "superadmin" ||
    (Array.isArray(savedUser.companies) &&
      savedUser.companies.some(
        (company: any) =>
          String(company.userType || "").trim().toLowerCase() ===
          "superadmin"
      ));

  // Identify the current page
  const isDashboard = location.pathname === "/dashboard";

  // Show Back and Create Contract only outside Dashboard
  const showBackAndCreateContract = !isDashboard;

  // Logout
  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userNo");
    localStorage.removeItem("userName");
    localStorage.removeItem("userType");
    localStorage.removeItem("authToken");

    navigate("/login", { replace: true });
  };

  // Back navigation
  const handleBack = () => {
    navigate(-1);
  };

  // Create Contract
  const handleCreateContract = () => {
    if (
      sessionNo == null ||
      !Number.isFinite(Number(sessionNo)) ||
      Number(sessionNo) <= 0 ||
      projectNo == null ||
      !Number.isFinite(Number(projectNo)) ||
      Number(projectNo) <= 0
    ) {
      alert(
        "Project number or session number is missing. Please return to Projects and open the project using the Contracts button."
      );
      return;
    }

    const params = new URLSearchParams({
      projectNo: String(projectNo),
      sessionNo: String(sessionNo),
    });

    navigate(`/create-tender?${params.toString()}`);
  };

  // Common navigation button styling
  const navButtonClass = `
    group relative inline-flex h-full items-center gap-2
    whitespace-nowrap px-3 text-[13px] font-normal
    text-slate-600 transition-colors duration-200
    hover:bg-slate-50 hover:text-[#53538c]
    focus-visible:outline-none focus-visible:ring-2
    focus-visible:ring-inset focus-visible:ring-[#53538c]
  `;

  const tabUnderlineClass = `
    absolute bottom-0 left-3 right-3 h-[2px]
    origin-center scale-x-0 bg-[#53538c]
    transition-transform duration-200
    group-hover:scale-x-100
  `;

  return (
    <div
      className="flex h-screen flex-col overflow-hidden bg-gray-100"
      style={{
        fontFamily: '"Segoe UI", sans-serif',
        fontSize: "13px",
        fontWeight: 400,
      }}
    >
      {/* COMMON HEADER */}
      <header className="z-50 h-[49px] flex-shrink-0 border-b border-slate-200 bg-white shadow-sm">
        <div className="flex h-12 w-full items-center justify-between px-5">

          {/* LEFT: BACK BUTTON - HIDDEN ON DASHBOARD */}
          <div className="flex min-w-0 items-center gap-3">
            {showBackAndCreateContract && (
              <button
                type="button"
                onClick={handleBack}
                className="flex h-8 w-8 flex-shrink-0 items-center
                           justify-center rounded-md text-slate-600
                           transition-colors hover:bg-slate-100
                           hover:text-[#53538c]
                           focus-visible:outline-none
                           focus-visible:ring-2
                           focus-visible:ring-[#53538c]"
                title="Go back"
                aria-label="Go back"
              >
                <ArrowLeft size={18} strokeWidth={1.8} />
              </button>
            )}
          </div>

          {/* RIGHT: NAVIGATION, USER AND LOGOUT */}
          <div className="flex h-full flex-shrink-0 items-center">

            {/* CREATE CONTRACT - HIDDEN ON DASHBOARD */}
            {showBackAndCreateContract && (
              <button
                type="button"
                onClick={handleCreateContract}
                className={navButtonClass}
              >
                <FilePlus2 size={16} strokeWidth={1.8} />
                <span>Create Contract</span>
                <span className={tabUnderlineClass} />
              </button>
            )}

            {/* SUPER ADMIN */}
            {isSuperAdmin && (
              <button
                type="button"
                onClick={() => navigate("/super-admin")}
                className={navButtonClass}
              >
                <ShieldCheck size={16} strokeWidth={1.8} />
                <span>Super Admin</span>
                <span className={tabUnderlineClass} />
              </button>
            )}

            {/* VERTICAL DIVIDER */}
            <div className="mx-2 h-6 w-px bg-slate-200" />

            {/* USERNAME */}
            <div
              className="flex items-center gap-2 rounded-md px-2 py-1.5"
              title={userName}
            >
              <div
                className="flex h-7 w-7 flex-shrink-0 items-center
                           justify-center rounded-full bg-slate-100"
              >
                <UserRound
                  size={15}
                  className="text-slate-600"
                  strokeWidth={1.8}
                />
              </div>

              <span
                className="max-w-40 truncate whitespace-nowrap
                           text-[13px] font-medium text-slate-700"
              >
                {userName}
              </span>
            </div>

            {/* DIVIDER BEFORE LOGOUT */}
            <div className="mx-2 h-6 w-px bg-slate-200" />

            {/* LOGOUT */}
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex h-8 items-center gap-1.5
                         whitespace-nowrap rounded-md px-2.5
                         text-[13px] text-slate-600
                         transition-colors hover:bg-slate-100
                         hover:text-[#53538c]
                         focus-visible:outline-none
                         focus-visible:ring-2
                         focus-visible:ring-[#53538c]"
              title="Logout"
              aria-label="Logout"
            >
              <LogOut size={16} strokeWidth={1.8} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* PAGE CONTENT */}
      <main className="min-h-0 flex-1 overflow-hidden">
        <div className="h-full min-h-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default MainViewLayout;
